import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { AuthenticationService } from '../../modules/services/authentication.service';
import { ApiServiceService } from '../../modules/services/api-service.service';
import alertify from 'alertifyjs';
import { Router } from '@angular/router';
import { UserProfileModalComponent, UserProfileViewModel } from '../user-profile-modal/user-profile-modal.component';
import { MdaSettingsModal } from "../mda-settings-modal/mda-settings-modal.component";
import { SettingsService } from '../../modules/services/settings/settings.service';
import { Subscription } from 'rxjs';
import { WorkflowNotification, WorkflowNotificationService } from '../../modules/services/workflow-notification.service';
@Component({
  selector: 'app-logged-in-header',
  standalone: true,
  imports: [CommonModule, UserProfileModalComponent, MdaSettingsModal],
  templateUrl: './logged-in-header.component.html',
  styleUrl: './logged-in-header.component.scss'
})
export class LoggedInHeaderComponent implements OnInit, OnDestroy {
  @Input() backgroundColor: string | null = null;
  @Input() dark = false;
  @Input() showBrand = false;
  @Input() brandLogoSrc = 'assets/svgs/logo.svg';
  @Input() brandAlt = 'VMS';
  mobileMenuOpen = false;
  dropdownOpen = false;
  profileOpen = false;
  settingsOpen = false;
  notificationOpen = false;
  isLoadingNotifications = false;
  unreadNotificationCount = 0;
  notifications: WorkflowNotification[] = [];
  private readonly subscriptions = new Subscription();

  get currentUser() {
    return this.authenticationService.getAuthenticatedUser();
  }

  get isMdaAdmin(): boolean {
    return this.authenticationService.hasRoles(["MDA_ADMIN"]);
  }

  get isAdmin(): boolean {
    return this.authenticationService.hasRoles(["ADMIN"]);
  }

  get initials(): string {
    const name = this.currentUser?.name?.trim() || 'User';
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? 'U';
    const second = parts[1]?.[0] ?? parts[0]?.[1] ?? '';
    return (first + second).toUpperCase();
  }

  get profileViewModel(): UserProfileViewModel {
    const user = this.currentUser;
    return {
      name: user?.name ?? 'Chijioke Okonkwo',
      email: user?.email ?? 'chijioke@anambra.gov.ng',
      roleLabel: user?.roles?.[0] ?? 'MDA Officer',
      mda: 'Ministry of Works',
      phoneNumber: '09076634225',
      dateRegistered: '15/01/2026',
      accountStatusTitle: 'Account Active',
      accountStatusSubtitle: 'Your account is in good standing',
      lastLoginText: 'Wednesday, 15 April 2026 at 15:00',
    };
  }

  get mdaId() {
    return this.authenticationService.getCurrentUserMda();
  }

  constructor(
      private apiService:ApiServiceService, 
      private authenticationService: AuthenticationService, 
      private router:Router,
      private readonly settingsService: SettingsService,
      private readonly workflowNotificationService: WorkflowNotificationService
) { }

  ngOnInit(): void {
    this.loadUnreadNotificationCount();
    this.loadNotifications();
    this.workflowNotificationService.connect();
    this.subscriptions.add(
      this.workflowNotificationService.workflowNotifications$.subscribe((notification) => {
        this.notifications = [notification, ...this.notifications.filter((item) => item.id !== notification.id)].slice(0, 20);
        this.unreadNotificationCount += notification.read ? 0 : 1;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  toggleDropdown(): void {
    this.notificationOpen = false;
    this.dropdownOpen = !this.dropdownOpen;
  }

  toggleNotifications(): void {
    this.dropdownOpen = false;
    this.notificationOpen = !this.notificationOpen;
    if (this.notificationOpen) {
      this.loadNotifications();
      this.loadUnreadNotificationCount();
    }
  }

  openProfile(): void {
    this.dropdownOpen = false;
    this.profileOpen = true;
  }

  closeProfile(): void {
    this.profileOpen = false;
  }

  openSettings(): void {
    this.dropdownOpen = false;
    this.settingsOpen = true;
  }

  closeSettings(): void {
    this.settingsOpen = false;
  }

  loadNotifications(): void {
    this.isLoadingNotifications = true;
    this.workflowNotificationService.fetchNotifications(1, 20, false).subscribe({
      next: (response) => {
        this.notifications = response?.data?.content ?? response?.data ?? [];
        this.isLoadingNotifications = false;
      },
      error: () => {
        this.notifications = [];
        this.isLoadingNotifications = false;
      },
    });
  }

  loadUnreadNotificationCount(): void {
    this.workflowNotificationService.fetchUnreadCount().subscribe({
      next: (response) => {
        this.unreadNotificationCount = Number(response?.data?.unreadCount ?? 0);
      },
      error: () => {
        this.unreadNotificationCount = 0;
      },
    });
  }

  markNotificationRead(notification: WorkflowNotification, event?: Event): void {
    event?.stopPropagation();
    if (!notification || notification.read) return;

    this.workflowNotificationService.markRead(notification.id).subscribe({
      next: (response) => {
        const updated = response?.data ?? { ...notification, read: true };
        this.notifications = this.notifications.map((item) =>
          item.id === notification.id ? { ...item, ...updated, read: true } : item,
        );
        this.unreadNotificationCount = Math.max(0, this.unreadNotificationCount - 1);
      },
      error: () => alertify.error('Unable to mark notification as read.'),
    });
  }

  markAllNotificationsRead(event?: Event): void {
    event?.stopPropagation();
    if (!this.notifications.length || this.unreadNotificationCount === 0) return;

    this.workflowNotificationService.markAllRead().subscribe({
      next: () => {
        this.notifications = this.notifications.map((item) => ({ ...item, read: true }));
        this.unreadNotificationCount = 0;
      },
      error: () => alertify.error('Unable to mark notifications as read.'),
    });
  }

  notificationTrackBy(_: number, notification: WorkflowNotification): string {
    return notification.id;
  }

  formatNotificationTime(value: string): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-NG', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  logout(): void {
    this.apiService.logout().subscribe({
      next: () => {
        this.workflowNotificationService.disconnect();
        this.authenticationService.clearAuthenticatedSession();
        this.closeMobileMenu();
        this.dropdownOpen = false;
        this.profileOpen = false;
        alertify.success('You have been logged out.');
        this.settingsService.resetColorScheme();
        void this.router.navigate(['/login']);
      },
      error: (error) => {
        this.workflowNotificationService.disconnect();
        this.authenticationService.clearAuthenticatedSession();
        this.closeMobileMenu();
        this.dropdownOpen = false;
        this.profileOpen = false;
        alertify.error(
          typeof error === 'string'
            ? error
            : 'We could not complete logout cleanly, but your local session has been cleared.',
        );
        void this.router.navigate(['/login']);
      },
    });
  }
  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('[data-profile-menu]')) return;
    if (target.closest('[data-notification-menu]')) return;
    this.dropdownOpen = false;
    this.notificationOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.dropdownOpen = false;
    this.notificationOpen = false;
    this.profileOpen = false;
  }

  navigateToSection(sectionId: string, event?: Event): void {
    event?.preventDefault();
    const target = document.getElementById(sectionId);
    if (!target) {
      void this.router.navigate(['/'], { fragment: sectionId });
      return;
    }

    const top = target.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top, behavior: 'smooth' });
    this.closeMobileMenu();
  }
}
